import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { Fare, FareSliceDeparture } from "@b2bportal/air-shopping-api";
import type {
  AppDispatch,
  IFlightListData,
  IPassengerCounts,
} from "@b2bportal/core-types";
import { CallState } from "@b2bportal/core-types";
import { getFunnelId } from "@b2bportal/core-utilities";
import { HtsAirCfarOffersActions } from "../store/hts";
import {
  selectRoundTripOutboundSyntheticSelected,
  selectReturnSliceBatchCfarOffersState,
  selectReturnSliceBatchCfarOffersData,
} from "../store/hts/selectors";
import type { FetchBatchCfarOffers } from "./createUseCfarFaresSource";

export interface UseReturnCfarBatchFetchProps {
  flightList: IFlightListData<Fare | FareSliceDeparture>[];
  passengers: IPassengerCounts;
  fetchBatchCfarOffers: FetchBatchCfarOffers;
  shouldPopulateFarePriceBreakdown: boolean;
}

/**
 * Generic hook to batch-fetch CFAR offers for return flights.
 * Can be used by any tenant by injecting their fetch function.
 *
 * Also manages the `shouldShowOnlySyntheticFares` flag:
 * - Sets to `true` when on return page with outbound synthetic selected
 * - Clears on unmount (when navigating away from return page)
 *
 * @returns Object containing:
 * - `isLoading`: Whether CFAR offers are currently being fetched
 * - `cfarEligibleFlightsList`: Filtered list of flights with CFAR offers (when outbound synthetic selected)
 */
export const useReturnFlightCfarBatchFetch = ({
  flightList,
  passengers,
  fetchBatchCfarOffers,
  shouldPopulateFarePriceBreakdown,
}: UseReturnCfarBatchFetchProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const lastRequestKeyRef = useRef<string | null>(null);

  const isOutboundSyntheticSelected = useSelector(
    selectRoundTripOutboundSyntheticSelected
  );
  const cfarOffersState = useSelector(selectReturnSliceBatchCfarOffersState);
  const cfarOffersData = useSelector(selectReturnSliceBatchCfarOffersData);

  const enabled = isOutboundSyntheticSelected && flightList.length > 0;

  useEffect(() => {
    if (enabled) {
      dispatch(HtsAirCfarOffersActions.setShouldShowOnlySyntheticFares(true));

      return () => {
        dispatch(
          HtsAirCfarOffersActions.setShouldShowOnlySyntheticFares(false)
        );
      };
    }
  }, [enabled, dispatch]);

  useEffect(() => {
    if (!enabled) return;

    const fareTrips = flightList.flatMap((flight) =>
      flight.fares
        .filter((fare): fare is Fare => "tripId" in fare && "id" in fare)
        .map((fare) => ({
          tripId: fare.tripId,
          fareId: fare.id,
          farePriceBreakdown: shouldPopulateFarePriceBreakdown
            ? fare.priceBreakdown
            : undefined,
        }))
    );

    const apiPassengers = {
      ADT: Number(passengers.adultsCount),
      ...(passengers.infantsInSeatCount
        ? { INS: Number(passengers.infantsInSeatCount) }
        : {}),
      ...(passengers.infantsOnLapCount
        ? { INF: Number(passengers.infantsOnLapCount) }
        : {}),
      ...(passengers.childrenCount
        ? { CNN: Number(passengers.childrenCount) }
        : {}),
    };

    // The funnel ID is read at request-build time and is part of the request
    // key, so a funnel rotation with unchanged trip/fare IDs refetches
    // instead of reusing the previous funnel's attribution.
    const funnelId = getFunnelId("air");

    const requestKey = JSON.stringify({
      fareTrips,
      passengers: apiPassengers,
      funnelId,
    });

    if (lastRequestKeyRef.current === requestKey) {
      return;
    }

    lastRequestKeyRef.current = requestKey;

    dispatch(
      HtsAirCfarOffersActions.fetchReturnSliceBatchCfarOffers({
        fareTrips,
        passengers: apiPassengers,
        funnelId,
        fetchBatchCfarOffers,
      })
    );
  }, [
    enabled,
    flightList,
    passengers,
    dispatch,
    fetchBatchCfarOffers,
    shouldPopulateFarePriceBreakdown,
  ]);

  const cfarEligibleFlightsList = useMemo(() => {
    if (!isOutboundSyntheticSelected) {
      return flightList;
    }

    return flightList.filter((flight) =>
      flight.fares.some((fare) => {
        const fareId = "id" in fare ? fare.id : null;
        return fareId ? cfarOffersData[fareId]?.length > 0 : false;
      })
    );
  }, [flightList, cfarOffersData, isOutboundSyntheticSelected]);

  const isLoading =
    isOutboundSyntheticSelected && cfarOffersState === CallState.InProcess;

  return {
    isLoading,
    cfarEligibleFlightsList,
  };
};
